import { useState, useEffect } from 'react';
import { Box, Container, Typography, TextField, Button, Snackbar, Alert } from '@mui/material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useEmailSubscription } from '../hooks/useEmailSubscription';

function setMetaDescription(content) {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'description');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function Newsletter() {
  const [email, setEmail] = useState('');
  const { loading, snackbar, setSnackbar, subscribeEmail } = useEmailSubscription();

  useEffect(() => {
    document.title = 'Newsletter | Huelmu';
    setMetaDescription(
      'Únete a nuestro newsletter y recibe novedades sobre Huelmu y los proyectos ambientales que impulsamos'
    );
    // Hacer scroll al inicio cuando se carga la página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await subscribeEmail(email, () => {
      setEmail('');
    }, true);
  };

  return (
    <Box
      component="main"
      className="newsletter-scope"
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom, #fafaf8 0%, #f5f5f0 100%)',
        position: 'relative',
        width: '100%',
        overflowX: 'hidden',
      }}
    >
      <Navbar />
      <Container 
        maxWidth="sm" 
        sx={{ 
          position: 'relative', 
          zIndex: 1,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            pt: { xs: 12, sm: 14, md: 16 },
            pb: { xs: 6, sm: 8 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
              fontWeight: 700,
              lineHeight: { xs: 1.25, sm: 1.2 },
              mb: { xs: 2, sm: 2.5 },
              color: 'text.primary',
            }}
          >
            Únete a nuestro Newsletter
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem' },
              mb: { xs: 3, sm: 4 },
              color: 'text.secondary',
              maxWidth: '500px',
              lineHeight: 1.6,
            }}
          >
            Recibe novedades sobre Huelmu y los proyectos ambientales que impulsamos.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: '100%',
              maxWidth: '450px',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box
              sx={{
                flex: 1,
                width: '100%',
                backgroundColor: 'white',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '8px',
                overflow: 'hidden',
                boxSizing: 'border-box',
                height: { xs: '48px', sm: '52px' },
                display: 'flex',
                alignItems: 'center',
                '&:focus-within': {
                  borderColor: 'rgba(0, 0, 0, 0.4)',
                },
              }}
            >
              <TextField
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
                sx={{
                  width: '100%',
                  boxSizing: 'border-box',
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '1rem', sm: '1.05rem' },
                    height: { xs: '48px', sm: '52px' },
                    minHeight: { xs: '48px', sm: '52px' },
                    borderRadius: 0,
                    overflow: 'visible',
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: 'transparent',
                    boxShadow: 'none !important',
                    outline: 'none !important',
                    border: 'none !important',
                    '& .MuiOutlinedInput-input': {
                      borderRadius: 0,
                      py: { xs: '12px', sm: '14px' },
                      px: { xs: '16px', sm: '20px' },
                      height: '100%',
                      boxShadow: 'none !important',
                      outline: 'none !important',
                      border: 'none !important',
                      backgroundColor: 'transparent',
                      appearance: 'none',
                      '&:focus': {
                        boxShadow: 'none !important',
                        outline: 'none !important',
                        border: 'none !important',
                      },
                      '&:focus-visible': {
                        boxShadow: 'none !important',
                        outline: 'none !important',
                        border: 'none !important',
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none !important',
                      display: 'none',
                    },
                    '& fieldset': {
                      border: 'none !important',
                      display: 'none !important',
                    },
                    '&:hover fieldset': {
                      border: 'none !important',
                      display: 'none !important',
                    },
                    '&.Mui-focused': {
                      boxShadow: 'none !important',
                      outline: 'none !important',
                      border: 'none !important',
                      '& fieldset': {
                        border: 'none !important',
                        display: 'none !important',
                      },
                    },
                    '&.Mui-focusVisible': {
                      boxShadow: 'none !important',
                      outline: 'none !important',
                      border: 'none !important',
                    },
                    '&.Mui-error fieldset': {
                      border: 'none !important',
                      display: 'none !important',
                    },
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                borderRadius: '8px',
                overflow: 'hidden',
                flexShrink: 0,
                height: { xs: '48px', sm: '52px' },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  px: { xs: 3, sm: 4 },
                  py: 0,
                  height: '100%',
                  backgroundColor: 'primary.main',
                  whiteSpace: 'nowrap',
                  border: 'none',
                  outline: 'none !important',
                  overflow: 'hidden',
                  borderRadius: '8px',
                  boxShadow: '0 4px 14px rgba(74, 106, 74, 0.4)',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    boxShadow: '0 6px 20px rgba(74, 106, 74, 0.5)',
                    outline: 'none !important',
                  },
                  '&:focus': {
                    boxShadow: '0 4px 14px rgba(74, 106, 74, 0.4)',
                    outline: 'none !important',
                  },
                  '&:focus-visible': {
                    boxShadow: '0 4px 14px rgba(74, 106, 74, 0.4)',
                    outline: 'none !important',
                  },
                  '&:disabled': {
                    backgroundColor: 'secondary.light',
                  },
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  width: { xs: '100%', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& .MuiButtonBase-root': {
                    overflow: 'hidden',
                    borderRadius: '8px',
                    outline: 'none !important',
                  },
                  '& .MuiButton-label': {
                    borderRadius: '8px',
                  },
                  '& .MuiTouchRipple-root': {
                    borderRadius: '8px',
                  },
                  '&::before': {
                    display: 'none',
                  },
                  '&::after': {
                    display: 'none',
                  },
                }}
              >
              {loading ? 'Suscribiendo...' : 'Suscribirme'}
            </Button>
          </Box>
          </Box>
        </Box>
      </Container>

      <Footer />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          bottom: { xs: 16, sm: 24 },
        }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: { xs: 'calc(100% - 32px)', sm: 'auto' },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: '400px' },
            fontSize: { xs: '0.9rem', sm: '1rem' },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Newsletter;

